export type SlideSavePatch = Partial<{
  objects: unknown;
  notes: string;
  backgroundColor: string;
  sectionTitle: string;
  layoutId: string;
  order: number;
}>;

export type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'error';

type SlideQueue = {
  pending: SlideSavePatch;
  timer: ReturnType<typeof setTimeout> | null;
  inFlight: Promise<void> | null;
  generation: number;
  error: string | null;
};

export function mergeSlidePatch(current: SlideSavePatch, next: SlideSavePatch): SlideSavePatch {
  return { ...current, ...next };
}

export function deriveSaveStatus(queues: Array<{ pending: boolean; saving: boolean; error: boolean }>): SaveStatus {
  if (queues.some((queue) => queue.error)) return 'error';
  if (queues.some((queue) => queue.saving)) return 'saving';
  if (queues.some((queue) => queue.pending)) return 'unsaved';
  return 'saved';
}

export function createSlideSaveQueue(options: {
  delayMs?: number;
  save: (slideId: string, patch: SlideSavePatch) => Promise<void>;
  onStatus?: (status: SaveStatus) => void;
  onError?: (message: string) => void;
}) {
  const delayMs = options.delayMs ?? 800;
  const queues = new Map<string, SlideQueue>();

  const statusOf = (queue: SlideQueue) => ({
    pending: Object.keys(queue.pending).length > 0,
    saving: Boolean(queue.inFlight),
    error: Boolean(queue.error),
  });

  const emitStatus = () => {
    options.onStatus?.(deriveSaveStatus(Array.from(queues.values()).map(statusOf)));
  };


  const ensure = (slideId: string): SlideQueue => {
    const existing = queues.get(slideId);
    if (existing) return existing;
    const created: SlideQueue = { pending: {}, timer: null, inFlight: null, generation: 0, error: null };
    queues.set(slideId, created);
    return created;
  };

  const run = async (slideId: string): Promise<void> => {
    const queue = ensure(slideId);
    if (queue.inFlight) {
      await queue.inFlight;
      if (Object.keys(queue.pending).length) return run(slideId);
      return;
    }
    if (!Object.keys(queue.pending).length) {
      emitStatus();
      return;
    }
    const generation = queue.generation + 1;
    queue.generation = generation;
    const patch = queue.pending;
    queue.pending = {};
    if (queue.timer) {
      clearTimeout(queue.timer);
      queue.timer = null;
    }
    const work = (async () => {
      try {
        await options.save(slideId, patch);
        if (queue.generation === generation) queue.error = null;
      } catch (error) {
        queue.pending = mergeSlidePatch(patch, queue.pending);
        queue.error = error instanceof Error ? error.message : 'Kunde inte spara sliden.';
        options.onError?.(queue.error);
        throw error;
      } finally {
        queue.inFlight = null;
        emitStatus();
      }
    })();
    queue.inFlight = work.catch(() => undefined);
    emitStatus();
    await queue.inFlight;
    if (Object.keys(queue.pending).length) await run(slideId);
  };

  const schedule = (slideId: string, patch: SlideSavePatch, immediate = false) => {
    const queue = ensure(slideId);
    queue.pending = mergeSlidePatch(queue.pending, patch);
    queue.error = null;
    if (queue.timer) clearTimeout(queue.timer);
    emitStatus();
    if (immediate) {
      queue.timer = null;
      void run(slideId);
      return;
    }
    queue.timer = setTimeout(() => {
      queue.timer = null;
      void run(slideId);
    }, delayMs);
  };

  const flushSlide = async (slideId: string) => {
    const queue = queues.get(slideId);
    if (!queue) return;
    if (queue.timer) {
      clearTimeout(queue.timer);
      queue.timer = null;
    }
    await run(slideId);
  };

  const flushAll = async () => {
    await Promise.all(Array.from(queues.keys()).map((slideId) => flushSlide(slideId)));
  };

  const hasUnsaved = () => Array.from(queues.values()).some((queue) => (
    Object.keys(queue.pending).length > 0 || Boolean(queue.inFlight) || Boolean(queue.timer)
  ));

  const dispose = () => {
    Array.from(queues.values()).forEach((queue) => {
      if (queue.timer) clearTimeout(queue.timer);
    });
    queues.clear();
  };

  return { schedule, flushSlide, flushAll, hasUnsaved, dispose, emitStatus };
}
